import { TwainGameId } from 'betgames-contract-js';
import WebSocketsRepository from 'repositories/WebSockets/WebSockets.repository';
import { IWSBetStatus } from 'store/Betslip/interfaces';
import { IRngResult, IRngMatchEvent } from 'store/RngGame/interfaces';
import { ITournamentMatchShotEvent } from 'services/WebSockets/interfaces/ITournamentMatchShotEvent';
import { ITournamentMatch } from 'store/TournamentMatch/interfaces';
import { IBettingOption } from 'interfaces/IBettingOption';
import { rooms } from './utils/rooms';
import {
    ITournamentMatchEvent,
    IZoneGeneratedEvent,
    IParticipantLifetimeStatistics,
    IParticipantsRank,
    ITournamentMatchRevealEvent,
    ITournamentUpdateEvent,
    IWSBettingOptions,
    IWSBettingOptionsRemoval,
    ITournamentMatchesEvent,
    IBetCalculateEvent,
    IBettingOptionCalculateEvent,
    IWSBalance,
    ITournamentUpdateStatisticsEvent,
    ITournamentUpdateMatchResultEvent,
    IBetWinningEvent,
} from './interfaces';

const CHANNELS = {
    TOURNAMENT_MATCH: (gameId: TwainGameId, matchCode: string) =>
        `game:${gameId}:tournament-match:${matchCode}`,
    TOURNAMENT_MATCHES: (gameId: TwainGameId, tournamentCode: string) =>
        `game:${gameId}:tournament:${tournamentCode}:tournament-match`,
    TOURNAMENT: (gameId: TwainGameId, tournamentCode: string) =>
        `game:${gameId}:tournament:${tournamentCode}`,
    ZONE_GENERATED: (gameId: TwainGameId) => `game:${gameId}:zones-generated`,
    LAST_RESULTS: (gameId: TwainGameId) => `game:${gameId}:last-results`,
    PARTICIPANTS_LIFETIME_STATISTICS: (gameId: TwainGameId) =>
        `game:${gameId}:participant-lifetime-statistics`,
    PARTICIPANTS_RANK: (gameId: TwainGameId) => `game:${gameId}:participants-rank`,
    BET_CALCULATE: () => 'bet:calculate',
    BET_WINNINGS: () => 'winnings',
    BETTING_OPTION_CALCULATE: () => 'betting-option:calculate',
    BET_PAY_IN: () => 'bet:pay-in',
    BALANCE: () => 'balance',
    BETTING_OPTIONS: (gameId: TwainGameId, presetId: number) =>
        `game:${gameId}:betting-options:${presetId}`,
    BETTING_OPTIONS_REMOVAL: (gameId: TwainGameId) => `game:${gameId}:betting-options-removal`,
    REFRESH_IFRAME: () => 'refresh-iframe',
    MATCH_STARTS_AT: (gameId: TwainGameId) => `game:${gameId}:match-starts-at`,
    ACTIVE_MATCH: (gameId: TwainGameId) => `game:${gameId}:active-match`,
};

const EMIT_CHANNELS = {
    MATCH_SCORES: 'match-scores',
    ZONE_GENERATED: 'zone-generated',
    MATCH_STARTS_AT: 'match-starts-at',
    ACTIVE_MATCH: 'active-match',
    LAST_RESULTS: 'last-results',
    BETTING_OPTIONS: 'betting-options',
};

class WebSocketsService {
    constructor(private readonly repository: WebSocketsRepository) {}

    public init = (url: string, jwt: string, presetsIds: Record<TwainGameId, number>) => {
        this.repository.init(url, jwt, presetsIds);
    };

    public tournamentMatch = (
        gameId: TwainGameId,
        matchCode: string,
        listener: ICallback<ITournamentMatchEvent>,
    ) => this.repository.on(CHANNELS.TOURNAMENT_MATCH(gameId, matchCode), listener); // shots and reveal match actions. Should be removed later

    public tournamentActiveMatch = (
        gameId: TwainGameId,
        listener: ICallback<ITournamentMatch | IRngMatchEvent | ITournamentMatchEvent>,
    ) => this.repository.on(CHANNELS.ACTIVE_MATCH(gameId), listener);

    public emitTournamentActiveMatch = (gameId: TwainGameId) =>
        this.repository.emit(EMIT_CHANNELS.ACTIVE_MATCH, { gameId });

    public tournament = (
        gameId: TwainGameId,
        tournamentCode: string,
        listener: ICallback<
            | ITournamentUpdateEvent
            | ITournamentUpdateMatchResultEvent
            | ITournamentUpdateStatisticsEvent
        >,
    ) => this.repository.on(CHANNELS.TOURNAMENT(gameId, tournamentCode), listener); // for tournament

    public tournamentMatches = (
        gameId: TwainGameId,
        tournamentCode: string,
        listener: ICallback<ITournamentMatchesEvent>,
    ) => this.repository.on(CHANNELS.TOURNAMENT_MATCHES(gameId, tournamentCode), listener); // all matches for tournament

    public zoneGenerated = (
        gameId: TwainGameId,
        matchCode: string,
        listener: ICallback<IZoneGeneratedEvent>,
    ) => {
        this.repository.emit(EMIT_CHANNELS.ZONE_GENERATED, { gameId, matchCode });

        return this.repository.on(CHANNELS.ZONE_GENERATED(gameId), listener);
    };

    public shot = (
        gameId: TwainGameId,
        matchCode: string,
        listener: ICallback<ITournamentMatchShotEvent | ITournamentMatchRevealEvent>,
    ) => {
        this.emitShots(gameId, matchCode);

        return this.repository.on(CHANNELS.TOURNAMENT_MATCH(gameId, matchCode), listener);
    };

    public emitShots = (gameId: TwainGameId, matchCode: string) => {
        this.repository.emit(EMIT_CHANNELS.MATCH_SCORES, { gameId, matchCode });
    };

    public matchStartsAt = (gameId: TwainGameId, listener: ICallback<number>) => {
        this.repository.emit(EMIT_CHANNELS.MATCH_STARTS_AT, { gameId });

        return this.repository.on(CHANNELS.MATCH_STARTS_AT(gameId), listener);
    };

    public participantsLifetimeStatistics = (
        gameId: TwainGameId,
        listener: ICallback<IParticipantLifetimeStatistics>,
    ) => this.repository.on(CHANNELS.PARTICIPANTS_LIFETIME_STATISTICS(gameId), listener);

    public participantsRank = (gameId: TwainGameId, listener: ICallback<IParticipantsRank>) =>
        this.repository.on(CHANNELS.PARTICIPANTS_RANK(gameId), listener);

    public betCalculate = (listener: ICallback<IBetCalculateEvent>) =>
        this.repository.on(CHANNELS.BET_CALCULATE(), listener);

    public betWinnings = (listener: ICallback<IBetWinningEvent>) =>
        this.repository.on(CHANNELS.BET_WINNINGS(), listener);

    public bettingOptionsCalculate = (listener: ICallback<IBettingOptionCalculateEvent>) =>
        this.repository.on(CHANNELS.BETTING_OPTION_CALCULATE(), listener);

    public betPayIn = (listener: ICallback<IWSBetStatus>) =>
        this.repository.on(CHANNELS.BET_PAY_IN(), listener);

    public balance = (listener: ICallback<IWSBalance>) =>
        this.repository.on(CHANNELS.BALANCE(), listener);

    public bettingOptions = (
        gameId: TwainGameId,
        presetId: number,
        listener: (data: IBettingOption[] | IWSBettingOptions) => void,
    ) => this.repository.on(CHANNELS.BETTING_OPTIONS(gameId, presetId), listener);

    public bettingOptionsRemoval = (
        gameId: TwainGameId,
        listener: ICallback<IWSBettingOptionsRemoval>,
    ) => this.repository.on(CHANNELS.BETTING_OPTIONS_REMOVAL(gameId), listener);

    public refreshIframe = (listener: ICallback) =>
        this.repository.on(CHANNELS.REFRESH_IFRAME(), listener);

    public subscribeGameRoom = (gameId: TwainGameId) =>
        this.repository.subscribe(rooms.getGameInfoRoom(gameId));

    public rngLastResult = (
        gameId: TwainGameId,
        listener: ICallback<IRngResult | IRngResult[]>,
    ) => {
        this.repository.emit(EMIT_CHANNELS.LAST_RESULTS, { gameId });

        return this.repository.on(CHANNELS.LAST_RESULTS(gameId), listener);
    };

    public rngBettingOptionsEmit = (gameId: TwainGameId, presetId: number) => {
        this.repository.emit(EMIT_CHANNELS.BETTING_OPTIONS, { gameId, presetId });
    };
}

export default WebSocketsService;
